/**
 * Auth store — manages user session, tokens, login/logout.
 *
 * Login persistence works two ways:
 * 1. Server-side (pywebview): GET /api/auth/session returns tokens from SQLite
 * 2. Client-side (browser): tokens in localStorage
 *
 * On init, we try server-side first, then fall back to localStorage.
 * This handles pywebview's ephemeral localStorage (Qt 5.15 bug).
 */

import { create } from "zustand";
import type { User } from "@/api/types";
import {
  apiFetch,
  clearTokens,
  loadStoredTokens,
  onAuthExpiredCallback,
  setTokens,
} from "@/api/client";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  init: () => Promise<void>;
  /** Poll the server for an active session. Returns true if authenticated. */
  checkSession: () => Promise<boolean>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  init: async () => {
    onAuthExpiredCallback(() => {
      set({ user: null, isAuthenticated: false, error: "Session expired" });
    });

    // Strategy 1: Check server-side session (works in pywebview where
    // localStorage is wiped on close)
    try {
      const res = await fetch("/api/auth/session");
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated) {
          setTokens(data.access_token, data.refresh_token);
          set({ user: data.user, isAuthenticated: true, isLoading: false });
          return;
        }
      }
    } catch {
      // Server might not be ready yet, fall through
    }

    // Strategy 2: Check localStorage (works in browser dev mode)
    const hasTokens = loadStoredTokens();
    if (!hasTokens) {
      set({ isLoading: false });
      return;
    }

    try {
      const user = await apiFetch<User>("/auth/me");
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      clearTokens();
      set({ isLoading: false });
    }
  },

  checkSession: async () => {
    try {
      const res = await fetch("/api/auth/session");
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated) {
          setTokens(data.access_token, data.refresh_token);
          set({ user: data.user, isAuthenticated: true, isLoading: false });
          return true;
        }
      }
    } catch { /* server not ready */ }
    return false;
  },

  loginWithGoogle: async (idToken: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: idToken }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Login failed" }));
        throw new Error(err.detail);
      }

      const data = await res.json();
      setTokens(data.access_token, data.refresh_token);
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  logout: async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      try {
        await apiFetch("/auth/logout", {
          method: "POST",
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        // Ignore
      }
    }
    clearTokens();
    set({ user: null, isAuthenticated: false });
  },
}));
